"use client"

import { useTranslations } from "next-intl"

import { ApolloPageShell, ApolloPageHeader } from "@/components/ui/apollo-page"
import { NeedsBookingFilters } from "@/components/dashboard/variants/frontdesk/needs-booking-filters"
import { NeedsBookingTable } from "@/components/dashboard/variants/frontdesk/needs-booking-table"
import { useNeedsBooking } from "@/components/dashboard/variants/frontdesk/use-needs-booking"

/**
 * Front-desk dashboard — the purpose-built variant for the `frontdesk` role.
 *
 * The Apollo header band over one table: the desk's whole queue of patients
 * waiting on an appointment. The generic board's stats strip, target/leaderboard
 * row and charts are deliberately absent — this is not a fallback and not a
 * stripped copy of anything. `GenericDashboard` remains untouched and is still
 * what front desk renders on every clinic where `frontdesk_dashboard_enabled`
 * is off.
 *
 * The header is the generic board's minus its month-year suffix — a bare
 * "Overview" eyebrow, matching the coordinator board. It renders at the fleet's
 * standard band size so this page's chrome matches every other page's; only the
 * board BELOW it runs tight. It carries no data of its own.
 *
 * The board's state lives HERE, in `useNeedsBooking`, rather than inside the
 * table — because its filters sit in the page header's action slot and its rows
 * in the panel below, and two sibling surfaces cannot each own the same counts.
 * The hook self-gates (care_team_enabled + appointments.create); both
 * halves read `allowed` from it, so on a clinic without the flag the header
 * renders bare and nothing below it exists. When it does render the board fills
 * the whole content area — this is the front desk's screen, not a widget on one
 * — so the page itself never scrolls; the table does.
 *
 * Dispatch does NOT come from the role walk: `frontdesk` is deliberately absent
 * from `resolveDashboardRole`'s PRIORITY (and its backend mirror
 * `RoleHierarchy::DASHBOARD_PRIORITY`), so it still resolves to `"generic"`.
 * The dispatcher in `app/(dashboard)/page.tsx` swaps this variant in on the
 * flag instead. That split is load-bearing twice over: `frontdesk` is a live
 * role on Dentex, Gjergji and Zaffiro too, which must keep the generic board;
 * and the `simple_clinic_mode` → `/calendar` redirect keys off the resolved
 * role being `"generic"`, so leaving PRIORITY alone preserves that redirect for
 * front desk for free.
 *
 * The HR check-in band is NOT declared here — it is cross-cutting and comes in
 * through `ApolloPageBandProvider` from the dispatcher, which `ApolloPageShell`
 * renders above `children`.
 */
export function FrontdeskDashboard() {
  const t = useTranslations("dashboard")
  // Owned HERE because two sibling surfaces read it: the filters in the header
  // and the table below. Neither can hold it without the other depending on it.
  const board = useNeedsBooking()

  return (
    <ApolloPageShell>
      {/* Header band */}
      <ApolloPageHeader
        eyebrow={t("page.eyebrow")}
        title={t("page.header")}
        // The board's filters ride in the header's action slot, opposite the
        // title, rather than in a band of their own — this page is one table,
        // so a separate filter row would be a strip of chrome above a strip of
        // chrome. Rendered only once the board is permitted, so an ungranted
        // user sees the header without controls that would do nothing.
        actions={board.allowed ? <NeedsBookingFilters board={board} /> : undefined}
      />

      {/* The board IS the page: one panel filling the rest of the shell's flex
          column. Nothing scrolls at this level — the table does. Like the
          filters above, it renders nothing without the flag/grant. */}
      <NeedsBookingTable board={board} />
    </ApolloPageShell>
  )
}
